import { m } from 'motion/react';
import type { ReactNode } from 'react';

import { cn } from '../lib/cn';

/**
 * `13 §4`: un estado vacío nombra la acción siguiente en vez de informar de que
 * no hay nada. «Aún no hay empresas» es un hecho; «Crea tu primera empresa» es
 * una pantalla de la que el usuario puede salir.
 *
 * El icono entra con muelle porque el estado vacío es lo primero que ve alguien
 * que acaba de crear su cuenta: si esa pantalla no tiene vida, el producto
 * parece apagado antes de haber empezado.
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
  tone = 'primary',
}: {
  icon?: ReactNode;
  title: string;
  description?: string | undefined;
  action?: ReactNode | undefined;
  tone?: 'primary' | 'danger' | 'muted';
}) {
  const tones = {
    primary: 'bg-primary-pale text-primary',
    danger: 'bg-danger-pale text-danger',
    muted: 'bg-muted text-ink-faint',
  }[tone];

  return (
    <m.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, ease: [0.22, 0.8, 0.28, 1] }}
      className="flex flex-col items-center justify-center rounded-xl border border-dashed border-line bg-paper px-6 py-14 text-center"
    >
      {icon ? (
        <m.span
          initial={{ scale: 0.6, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 340, damping: 22, delay: 0.06 }}
          className={cn('mb-5 grid size-14 place-items-center rounded-xl [&>svg]:size-6', tones)}
          aria-hidden="true"
        >
          {icon}
        </m.span>
      ) : null}

      <p className="text-base font-semibold text-ink">{title}</p>
      {description ? <p className="mt-2 max-w-md text-sm text-ink-soft">{description}</p> : null}
      {action ? <div className="mt-6 flex flex-wrap justify-center gap-2">{action}</div> : null}
    </m.div>
  );
}
